import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRequirementAuthoringOptions,
  IRequirementConstraintDetail,
  RequirementConstraintRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";
import { ConstraintTypeValue, selectedEnumValue } from "./lookupEnums";

type ConstraintValues = {
  constraintTypeId?: string;
  logicalGroup?: number | string;
  topicCategoryId?: string;
  formatTypeId?: string;
  accreditorId?: string;
  audienceDesignator?: string;
};

const { Form } = withTypes<ConstraintValues>();

type ConstraintDialogProps = {
  requirementId: string;
  constraint?: IRequirementConstraintDetail;
  options?: IRequirementAuthoringOptions;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const AUDIENCE_OPTIONS = ["", "P", "T"];

const ConstraintDialog = ({
  requirementId,
  constraint,
  options,
  open,
  onClose,
  onSaved,
}: ConstraintDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementService } = useGlobalContext();

  const handleSubmit = async (values: ConstraintValues) => {
    try {
      const constraintType = selectedEnumValue(
        options?.constraintTypes,
        values.constraintTypeId
      );

      // Exactly one typed value belongs to a constraint, decided by its type: the database enforces
      // that, so the other three are dropped rather than sent as extras.
      const response = await requirementService.saveRequirementConstraint(
        RequirementConstraintRequest.fromJS({
          id: constraint?.id,
          requirementId,
          constraintTypeId: values.constraintTypeId,
          logicalGroup: Number(values.logicalGroup) || 0,
          topicCategoryId:
            constraintType === ConstraintTypeValue.SubjectArea
              ? values.topicCategoryId || undefined
              : undefined,
          formatTypeId:
            constraintType === ConstraintTypeValue.Format
              ? values.formatTypeId || undefined
              : undefined,
          accreditorId:
            constraintType === ConstraintTypeValue.Source
              ? values.accreditorId || undefined
              : undefined,
          audienceDesignator:
            constraintType === ConstraintTypeValue.AudienceDesignator
              ? values.audienceDesignator || undefined
              : undefined,
        })
      );

      ensureOk(response);
      onSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          constraintTypeId:
            constraint?.constraintTypeId ?? options?.constraintTypes?.[0]?.id ?? "",
          logicalGroup: constraint?.logicalGroup ?? 1,
          topicCategoryId: constraint?.topicCategoryId ?? "",
          formatTypeId: constraint?.formatTypeId ?? "",
          accreditorId: constraint?.accreditorId ?? "",
          audienceDesignator: constraint?.audienceDesignator ?? "",
        }}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const constraintType = selectedEnumValue(
            options?.constraintTypes,
            values.constraintTypeId
          );

          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle>
                {t({
                  id: constraint ? "editRequirementConstraint" : "addRequirementConstraint",
                })}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="constraintTypeId">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          label={t({ id: "constraintType" })}
                          {...input}
                        >
                          {(options?.constraintTypes ?? []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Field>

                  <Field name="logicalGroup">
                    {({ input }) => (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "logicalGroup" })}
                          InputProps={{ type: "number" }}
                          helperText={t({ id: "logicalGroupHelper" })}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {/* Only the value that matches the chosen constraint type is offered. */}
                  {constraintType === ConstraintTypeValue.SubjectArea && (
                    <Field name="topicCategoryId">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <TextField
                            select
                            variant="standard"
                            fullWidth
                            label={t({ id: "subjectAreaLabel" })}
                            {...input}
                          >
                            {(options?.topicCategories ?? []).map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {constraintType === ConstraintTypeValue.Format && (
                    <Field name="formatTypeId">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <TextField
                            select
                            variant="standard"
                            fullWidth
                            label={t({ id: "formatLabel" })}
                            {...input}
                          >
                            {(options?.formatTypes ?? []).map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {constraintType === ConstraintTypeValue.Source && (
                    <Field name="accreditorId">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <TextField
                            select
                            variant="standard"
                            fullWidth
                            label={t({ id: "accreditorLabel" })}
                            {...input}
                          >
                            {(options?.accreditors ?? []).map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {constraintType === ConstraintTypeValue.AudienceDesignator && (
                    <Field name="audienceDesignator">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <TextField
                            select
                            variant="standard"
                            fullWidth
                            label={t({ id: "audienceLabel" })}
                            {...input}
                          >
                            {AUDIENCE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option || t({ id: "noneLabel" })}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </Field>
                  )}

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button disabled={submitting} onClick={onClose}>
                  {t({ id: "cancelButton" })}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: constraint ? "saveButton" : "addConstraint" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

export default ConstraintDialog;
